import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BookingPaymentHistoryItem } from '@/shared/api';
import { stripeAmount, stripeDate } from './stripePaymentPresentation';

export function StripePaymentHistoryRow({ row }: { row: BookingPaymentHistoryItem }) {
  const { t, i18n } = useTranslation('stripePayments');
  return (
    <li className="border-hairline rounded-tile flex min-w-0 flex-col gap-3 border bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-ink font-bold">{t(`purposes.${row.purpose}`)}</h3>
        <span className="bg-paper rounded-pill px-3 py-1 text-xs font-semibold">
          {t(`statuses.${row.status}`)}
        </span>
      </div>
      <dl className="grid min-w-0 grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-ink-soft text-xs">{t('original')}</dt>
          <dd className="font-mono font-semibold">
            {stripeAmount(row.amountMinor, row.currency, i18n.language)}
          </dd>
        </div>
        <div>
          <dt className="text-ink-soft text-xs">{t('refunded')}</dt>
          <dd className="font-mono">
            {stripeAmount(row.refundedAmountMinor, row.currency, i18n.language)}
          </dd>
        </div>
      </dl>
      {row.stripePaymentIntentId && (
        <div className="min-w-0 text-sm">
          <p className="text-ink-soft text-xs">{t('paymentIntentId')}</p>
          <code className="select-all break-all font-mono">{row.stripePaymentIntentId}</code>
        </div>
      )}
      <p className="text-ink-soft flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span>
          {t('created')}:{' '}
          <time dateTime={row.createdAt}>{stripeDate(row.createdAt, i18n.language)}</time>
        </span>
        {row.paidAt && (
          <span>
            {t('paid')}: <time dateTime={row.paidAt}>{stripeDate(row.paidAt, i18n.language)}</time>
          </span>
        )}
      </p>
      {row.dashboardUrl ? (
        <a
          href={row.dashboardUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink rounded-pill border-hairline focus-visible:outline-forest flex min-h-11 items-center justify-center gap-2 border px-4 text-sm font-bold focus-visible:outline focus-visible:outline-2"
        >
          {t('open')}
          <ExternalLink size={15} aria-hidden />
        </a>
      ) : (
        <p className="text-ink-soft break-words text-xs">
          {t(`reasons.${row.dashboardUnavailableReason}`, {
            defaultValue: t('reasons.account_unverified'),
          })}
        </p>
      )}
    </li>
  );
}
